import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { AREA_ADMIN, type ItemDeNavegacao } from '@/nucleo/navegacao/mapa';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { EmptyState } from '@/ui/EmptyState';

import { useResumoDosCadastros } from './cadastros.dados';
import { contagemDoDominio, ehCadastroDaApi, HUBS, type ChaveDeContagem } from './hubs';

const RAIZ = '/admin/parametros';

export interface PaginaDeHubProps {
  /** Caminho do hub no mapa. Dele saem título, ícone e as telas filhas. */
  caminho: string;
  /** Domínio de cada tela filha, para o contador do cartão. Sem entrada, sem contador. */
  dominioPorTela?: Readonly<Record<string, ChaveDeContagem>>;
  /**
   * Frase de apoio por tela, como o protótipo tem no hub de Cadastros de
   * Referência ("Unidades escolares (com código EOL)..."). Os outros hubs do
   * protótipo não têm essa frase, por isso é opcional.
   */
  descricaoPorTela?: Readonly<Record<string, string>>;
  /**
   * Como o protótipo conta cada hub — não é igual nos quatro:
   * - `'total'` (padrão): "N registro(s)", sem distinguir ativo de inativo.
   * - `'apenas-ativos'`: "N registro(s) ativo(s))", como o hub de Cadastros &
   *   Domínios.
   * - `'ativos-e-total'`: "N ativos · M no total", como o hub de Cadastros de
   *   Referência.
   */
  formatoContador?: 'total' | 'apenas-ativos' | 'ativos-e-total';
}

/**
 * Página de um hub de parametrização: lista as telas que ele reúne.
 *
 * As TELAS FILHAS SAEM DO MAPA de navegação, nunca de lista escrita aqui — é o
 * que garante que o cartão desta página e o submenu da barra lateral mostrem
 * exatamente o mesmo conjunto. Acrescentar uma tela no mapa a faz aparecer nos
 * dois lugares sem tocar neste arquivo.
 *
 * Serve os quatro hubs porque eles diferem só em título, resumo e telas — o
 * CLAUDE.md manda extrair componente na segunda repetição, e aqui seriam quatro.
 */
export function PaginaDeHub({
  caminho,
  dominioPorTela = {},
  descricaoPorTela = {},
  formatoContador = 'total',
}: PaginaDeHubProps) {
  const hub = AREA_ADMIN.grupos
    .flatMap((grupo) => grupo.itens)
    .find((item): item is ItemDeNavegacao => item.caminho === caminho);

  const telas = hub?.filhos ?? [];
  const Icone = hub?.icone;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={RAIZ}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Centro de Parametrização
        </Link>
      </div>

      <header className="flex items-start gap-3">
        {Icone && (
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
            aria-hidden="true"
          >
            <Icone className="h-5 w-5" />
          </span>
        )}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">{hub?.titulo ?? 'Hub'}</h1>
          <p className="text-sm text-muted-foreground">{HUBS[caminho]?.resumo}</p>
        </div>
      </header>

      {telas.length === 0 ? (
        <EmptyState
          title="Nenhuma tela neste grupo"
          description="Assim que uma tela entrar no mapa de navegação, ela aparece aqui."
        />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {telas.map((tela) => {
            const IconeDaTela = tela.icone;
            const dominio = dominioPorTela[tela.caminho];
            const descricaoDaTela = descricaoPorTela[tela.caminho];

            return (
              <li key={tela.caminho}>
                {/*
                  Card inteiro é o link, como o protótipo faz nos dois hubs
                  (`<Link className="group">` envolvendo o `<Card>`) — regra 9
                  das regras de interface: sem botão "Ver"/"Abrir" separado.
                */}
                <Link to={tela.caminho} className="block h-full">
                  <Card className="h-full transition-colors hover:border-primary/40">
                    <CardHeader className="pb-2">
                      <div className="flex items-start gap-2.5">
                        <IconeDaTela
                          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <CardTitle className="text-sm leading-snug">{tela.titulo}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {descricaoDaTela && (
                        <CardDescription className="text-sm">{descricaoDaTela}</CardDescription>
                      )}
                      {dominio !== undefined && (
                        <ContadorDoCartao dominio={dominio} formato={formatoContador} />
                      )}
                    </CardContent>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

type Formato = NonNullable<PaginaDeHubProps['formatoContador']>;

function textoDoContador(formato: Formato, contagem: { total: number; ativos: number }): string {
  if (formato === 'ativos-e-total') return `${contagem.ativos} ativos · ${contagem.total} no total`;
  if (formato === 'apenas-ativos') {
    return `${contagem.ativos} ${contagem.ativos === 1 ? 'registro ativo' : 'registros ativos'}`;
  }
  return `${contagem.total} ${contagem.total === 1 ? 'registro' : 'registros'}`;
}

/**
 * Contador do cartão. Cadastro que já lê a API pergunta o resumo ao servidor (e
 * só aparece quando ele chega: nada de "0 registros" enquanto carrega); o que
 * ainda lê o dado temporário conta na hora. A consulta fica num componente à
 * parte para que hub sem cadastro da API não precise de provedor de consulta.
 */
function ContadorDoCartao({ dominio, formato }: { dominio: ChaveDeContagem; formato: Formato }) {
  if (ehCadastroDaApi(dominio)) return <ContadorDaApi dominio={dominio} formato={formato} />;
  const contagem = contagemDoDominio(dominio, undefined);
  return contagem ? (
    <CardDescription className="text-xs">{textoDoContador(formato, contagem)}</CardDescription>
  ) : null;
}

function ContadorDaApi({ dominio, formato }: { dominio: ChaveDeContagem; formato: Formato }) {
  const resumo = useResumoDosCadastros();
  const contagem = contagemDoDominio(dominio, resumo.data);
  return contagem ? (
    <CardDescription className="text-xs">{textoDoContador(formato, contagem)}</CardDescription>
  ) : null;
}

export default PaginaDeHub;
